import { useState, type ReactNode } from 'react';
import { Button, Dialog, Field, Input, Textarea } from '@paige/ui';
import { useFormat } from '@paige/format';
import { runwayWord } from './account-words';
import './insights.css';

export interface CreditChange {
  /** Signed: positive adds, negative deducts. */
  add: number;
  reason: string;
}

export interface CreditDialogProps {
  balance: number;
  burnPerDay: number;
  /** The staff member's name, as Phone Home will show it. */
  actor: string;
  busy: boolean;
  onClose: () => void;
  onSubmit: (change: CreditChange) => void;
  /** Support's goodwill: add only, up to this many a company over a rolling 30 days (the API holds the same cap). */
  goodwillCap?: number | undefined;
  /** What is left of the cap for this company now (TenantCredits.goodwillLeft); unknown while it loads. */
  goodwillLeft?: number | undefined;
}

const MIN_REASON = 8;

function Preview(props: {
  balance: number;
  burn: number;
  amount: number;
  actor: string;
}): ReactNode {
  const { balance, burn, amount, actor } = props;
  const f = useFormat();
  const runway = (b: number) => runwayWord(b <= 0 ? 0 : burn > 0 ? Math.floor(b / burn) : null);
  return (
    <p className="ad-preview" aria-live="polite">
      Balance {f.int(Math.round(balance))} → <strong>{f.int(Math.round(balance + amount))}</strong>;
      runway {runway(balance)} → {runway(balance + amount)}. It goes on the page ledger Phone Home
      reads, shown as &ldquo;{amount >= 0 ? 'add' : 'deduct'}, by {actor}&rdquo; with your reason.
    </p>
  );
}

interface Draft {
  direction: 'add' | 'deduct';
  amount: string;
  reason: string;
}

/** The whole credits the draft asks for, or 0 when it is not a positive whole number. */
const creditsOf = (d: Draft) => {
  const n = Math.round(Number(d.amount));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

function DirectionSwitch({ draft, set }: { draft: Draft; set: (d: Draft) => void }): ReactNode {
  return (
    <div className="ad-switch" role="group" aria-label="Add or deduct">
      {(['add', 'deduct'] as const).map((d) => (
        <button
          key={d}
          type="button"
          aria-pressed={draft.direction === d}
          onClick={() => set({ ...draft, direction: d })}
        >
          {d === 'add' ? 'Add' : 'Deduct'}
        </button>
      ))}
    </div>
  );
}

/** "250 of this month's 500 goodwill left": the rolling 30 days, said as support says it. */
function goodwillHint(cap: number, left: number | undefined): string {
  const head =
    left === undefined ? `Goodwill: up to ${cap}` : `${left} of this month's ${cap} goodwill left`;
  return `${head} (a rolling 30 days); billing makes larger changes`;
}

function Fields(x: {
  draft: Draft;
  set: (d: Draft) => void;
  cap?: number | undefined;
  left?: number | undefined;
}): ReactNode {
  const { draft, set, cap, left } = x;
  const max = cap ? Math.min(cap, left ?? cap) : undefined;
  return (
    <>
      {cap ? null : <DirectionSwitch draft={draft} set={set} />}
      <Field label="Pages" required {...(cap ? { hint: goodwillHint(cap, left) } : {})}>
        {({ id }) => (
          <Input
            id={id}
            type="number"
            min={1}
            {...(max !== undefined ? { max } : {})}
            value={draft.amount}
            onChange={(e) => set({ ...draft, amount: e.target.value })}
          />
        )}
      </Field>
      <Field
        label="Reason"
        required
        hint={`At least ${MIN_REASON} characters; Phone Home shows it`}
      >
        {({ id }) => (
          <Textarea
            id={id}
            value={draft.reason}
            onChange={(e) => set({ ...draft, reason: e.target.value })}
          />
        )}
      </Field>
    </>
  );
}

function DialogActions(x: {
  draft: Draft;
  n: number;
  valid: boolean;
  signed: number;
  props: CreditDialogProps;
}): ReactNode {
  const verb = x.draft.direction === 'add' ? 'Add' : 'Deduct';
  return (
    <>
      <Button onClick={x.props.onClose}>Cancel</Button>
      <Button
        variant="primary"
        disabled={!x.valid}
        loading={x.props.busy}
        onClick={() => x.props.onSubmit({ add: x.signed, reason: x.draft.reason.trim() })}
      >
        {x.n > 0 ? `${verb} ${x.n}` : verb}
      </Button>
    </>
  );
}

/**
 * Add or deduct credits with a reason and a preview of the balance and runway after (round 1: sales
 * M4, support M5). Billing and engineering only; the API refuses anyone else. Writes through the same
 * ledger as Phone Home, in Phone Home's own form, so the change shows there at once.
 */
export function CreditDialog(props: CreditDialogProps): ReactNode {
  const [draft, setDraft] = useState<Draft>({ direction: 'add', amount: '', reason: '' });
  const n = creditsOf(draft);
  const signed = draft.direction === 'add' ? n : -n;
  const cap = props.goodwillCap;
  const limit = cap ? Math.min(cap, props.goodwillLeft ?? cap) : Infinity;
  const valid = n > 0 && n <= limit && draft.reason.trim().length >= MIN_REASON;
  const actions = <DialogActions draft={draft} n={n} valid={valid} props={props} signed={signed} />;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && props.onClose()}
      title={cap ? 'Goodwill pages' : 'Adjust pages'}
      size="sm"
      description="Every change is recorded with your name and the reason."
      actions={actions}
    >
      <div className="pui-form">
        <Fields draft={draft} set={setDraft} cap={cap} left={props.goodwillLeft} />
        {n > 0 ? (
          <Preview
            balance={props.balance}
            burn={props.burnPerDay}
            amount={signed}
            actor={props.actor}
          />
        ) : null}
      </div>
    </Dialog>
  );
}
